import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map, skip } from 'rxjs';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import type { MaskitoOptions } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoDateMode, maskitoDateOptionsGenerator } from '@maskito/kit';
import { UplotRange } from '../components/graph-uplot/graph/models/UplotRange.model';
import { getRangeString } from '../components/graph-uplot/graph/utils/uplotUtil';
import { LocaleSessionService } from '../services/localeSession.service';

@Component({
  selector: 'app-range-picker',
  templateUrl: './range-picker.component.html',
  styleUrls: ['./range-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [MatFormFieldModule, MatDatepickerModule, ReactiveFormsModule, MaskitoDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [MatDatepickerModule],
})
export class RangePickerComponent {
  protected localeSession = inject(LocaleSessionService);

  // Inserts the separators while typing
  protected readonly maskOptions: MaskitoOptions = maskitoDateOptionsGenerator({
    mode: this.localeSession.getDateFormat().toLocaleLowerCase() as MaskitoDateMode,
    separator: this.localeSession.getDateSeparator(),
  });

  private blockRangeChangeTrigger = false;

  typingInInput = false;

  rangeForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  @Input() mod = 1e3;

  private _currentRange: UplotRange;

  @Input() set currentRange(range: UplotRange) {
    if (!range) return;

    this.blockRangeChangeTrigger = true;
    this._currentRange = range;

    // Do not overwrite what is being typed: the emitted range comes straight back here and
    // setValue() re-formats the input under the caret.
    if (!this.typingInInput) {
      this.rangeForm.controls.start.setValue(range ? new Date(range.min * this.mod) : null);
      this.rangeForm.controls.end.setValue(range ? new Date(range.max * this.mod) : null);
    }
    setTimeout(() => (this.blockRangeChangeTrigger = false), 100);
  }

  get currentRange(): UplotRange {
    return this._currentRange;
  }

  @Input() disabled: boolean;

  @Output() rangeHasChanged = new EventEmitter<UplotRange>();

  constructor() {
    // private localeSession: LocaleSessionService, // private dateAdapter: DateAdapter<Date>,
    // this.dateAdapter.setLocale(localeSession.locale); //dd/MM/yyyy
    this.rangeForm.valueChanges
      .pipe(
        // filter((o) => o.start != null && o.end != null),
        skip(1),
        map(() => ({
          min: this.rangeForm.controls.start.value
            ? new Date(this.rangeForm.controls.start.value)
            : null,
          max: this.rangeForm.controls.end.value
            ? new Date(this.rangeForm.controls.end.value)
            : null,
        })),
        takeUntilDestroyed(),
        debounceTime(100),
        filter(() => !this.blockRangeChangeTrigger || this.typingInInput),
      )
      .subscribe(() => {
        // console.log('form change, trigger range change', change);
        this.rangeChanged();
      });
  }

  rangeChanged() {
    const [min, max] = [
      this.rangeForm.controls.start.value ? new Date(this.rangeForm.controls.start.value) : null,
      this.rangeForm.controls.end.value ? new Date(this.rangeForm.controls.end.value) : null,
    ];

    if (!min || !max) return;

    min.setHours(0, 0, 0);
    max.setHours(24, 0, -1);

    const range = { min: min.getTime() / this.mod, max: max.getTime() / this.mod };
    this.currentRange = range;
    this.rangeHasChanged.emit(range);
  }

  protected readonly getRangeString = getRangeString;
}
